import { isMockAuthEnabled } from '@/config/authMode';

/**
 * PNET's copy of the current bearer token (historically `localStorage.access_token`).
 *
 * Cognito (`local` and `cookie` session modes): the copy lives in page memory
 * only. Amplify already keeps the session (`local`: its own localStorage keys;
 * `cookie`: memory, with the refresh token in the identity service's httpOnly
 * cookie) and `ApiProvider.getAuthToken()` reads it from there, so a second copy
 * in web storage would only widen what a script running in the page can pick up.
 *
 * Mock auth (local runtime only): the backend-issued dev JWT has no other home,
 * so it stays in `localStorage.access_token` as before.
 */
const KEY = 'access_token';
let memoryCopy: string | null = null;

export const readAccessTokenCopy = (): string | null => {
  if (!isMockAuthEnabled) return memoryCopy;
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
};

export const storeAccessTokenCopy = (token: string): void => {
  if (!isMockAuthEnabled) {
    memoryCopy = token;
    return;
  }
  try {
    localStorage.setItem(KEY, token);
  } catch {
    /* ignore storage failures */
  }
};

export const clearAccessTokenCopy = (): void => {
  memoryCopy = null;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Synchronous "there is a session" hint for route guards and probes. The server
 * decides on the first API call: a dead session makes the session probe
 * (`ensureSessionAlive`) send the user to the login page with the
 * "session expired" notice.
 *
 * - mock: the stored dev JWT;
 * - Cognito (`local` and `cookie`): the stored user profile. It is written and
 *   cleared together with the session (`signInUserWithUserData`, logout, the
 *   unauthorized handler), as the old `localStorage.access_token` copy was, and
 *   it outlives an Amplify session that Amplify itself cleared, so that case
 *   still ends on the login page instead of a page of failing calls.
 */
export const hasSessionHint = (): boolean => {
  if (isMockAuthEnabled) return Boolean(readAccessTokenCopy());
  try {
    return Boolean(localStorage.getItem('userData'));
  } catch {
    return false;
  }
};

// A copy left in localStorage by an older build (or by `local` mode before a
// switch to `cookie`): drop it. Mock mode keeps its token there on purpose.
if (!isMockAuthEnabled) {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
